import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ActivityPreview } from "../activities/ActivityPreview";
import {
  activityProjectName,
  CREATIVE_CHALLENGES,
  GUIDED_ACTIVITIES,
} from "../activities/activityCatalog";
import { serializeActivityRoute } from "../activities/activityRoute";
import type { DrawingActivity } from "../activities/activityTypes";
import { Brand } from "../components/Brand";
import { useParentMode } from "../parent/ParentModeContext";
import { createProject } from "../projects/createProject";
import { saveProject } from "../storage/db";

interface ActivityCardProps {
  activity: DrawingActivity;
  isStarting: boolean;
  onStart: (activity: DrawingActivity) => void;
}

function ActivityCard({ activity, isStarting, onStart }: ActivityCardProps) {
  const titleId = `activity-${activity.id}-title`;
  return (
    <article className="activity-card" aria-labelledby={titleId}>
      <div className="activity-card__art">
        <ActivityPreview activity={activity} />
      </div>
      <div className="activity-card__body">
        <div className="activity-card__meta">
          <span
            className={`activity-difficulty activity-difficulty--${activity.difficulty.toLowerCase()}`}
          >
            {activity.difficulty}
          </span>
          {activity.type === "guided" ? <span>{activity.steps.length} steps</span> : null}
        </div>
        <h3 id={titleId}>{activity.title}</h3>
        <p>{activity.description}</p>
        {activity.type === "challenge" ? (
          <p className="activity-card__prompt">{activity.prompt}</p>
        ) : null}
        <button
          aria-label={`Start ${activity.title}`}
          className="button button--primary button--large"
          disabled={isStarting}
          onClick={() => onStart(activity)}
          type="button"
        >
          {isStarting ? "Starting..." : "Start"}
        </button>
      </div>
    </article>
  );
}

export function ActivitiesScreen() {
  const navigate = useNavigate();
  const parent = useParentMode();
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const startActivity = async (activity: DrawingActivity): Promise<void> => {
    if (startingId) return;
    if (!parent.can("useActivities")) {
      parent.askParent("Ask a parent before starting a Drawing Activity.");
      await navigate("/", { replace: true });
      return;
    }
    setStartingId(activity.id);
    setError("");
    try {
      const project = createProject({
        name: activityProjectName(activity),
        type: "drawing",
        orientation: "square",
        presetId: "square-1600",
      });
      await saveProject(project);
      const search = serializeActivityRoute(activity, 1);
      await navigate(`/studio/${project.id}?${search}`);
    } catch {
      setError("We couldn't start that activity. Check your device storage and try again.");
      setStartingId(null);
    }
  };

  return (
    <main className="activities-screen">
      <header className="simple-header activities-header">
        <Brand compact />
        <Link className="button button--secondary button--small" to="/">
          ← My projects
        </Link>
      </header>

      <section className="activities-hero" aria-labelledby="activities-title">
        <p className="eyebrow">Draw, learn and imagine</p>
        <h1 id="activities-title">Drawing Activities</h1>
        <p>Follow a friendly guide or choose a creative idea. Your drawing is always your own.</p>
      </section>

      {error ? (
        <p className="form-error activities-error" role="alert">
          {error}
        </p>
      ) : null}

      <section className="activity-section" aria-labelledby="learn-to-draw-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Simple visual steps</p>
            <h2 id="learn-to-draw-title">Learn to Draw</h2>
          </div>
          <span className="activity-count">6 activities</span>
        </div>
        <div className="activity-grid">
          {GUIDED_ACTIVITIES.map((activity) => (
            <ActivityCard
              activity={activity}
              isStarting={startingId === activity.id}
              key={activity.id}
              onStart={(selected) => void startActivity(selected)}
            />
          ))}
        </div>
      </section>

      <section className="activity-section" aria-labelledby="creative-challenges-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Big ideas, no scores</p>
            <h2 id="creative-challenges-title">Creative Challenges</h2>
          </div>
          <span className="activity-count">12 challenges</span>
        </div>
        <div className="activity-grid">
          {CREATIVE_CHALLENGES.map((activity) => (
            <ActivityCard
              activity={activity}
              isStarting={startingId === activity.id}
              key={activity.id}
              onStart={(selected) => void startActivity(selected)}
            />
          ))}
        </div>
      </section>

      <footer className="activities-footer">Offline, private and made for creative play.</footer>
    </main>
  );
}
